import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useHistory } from 'react-router-dom';

import { CodeOutlined } from '@ant-design/icons';
import { Button, Divider, notification, Switch } from 'antd';

import TemplateOrbit from 'assets/images/template-orbit.png';
import { useEventDetail } from 'components/Pages/Admin/TabsMyEvents/context/EventDetail';
import CustomLoader from 'components/Shared/CustomLoader';
import { TextAndSubText } from 'components/Shared/TextAndSubText';
import Tour from 'components/Shared/Tour';
import { getTargetElement } from 'components/Shared/Tour/helpers';
import { TourSteps } from 'components/Shared/Tour/types';
import { isAfter } from 'date-fns/isAfter';
import useTabTour from 'hooks/useTabTour';
import { IEventPost } from 'interfaces/event';
import ReactPlaceholder from 'react-placeholder/lib';
import eventService from 'services/event';

import { PageParams } from '../BasicData/types';
import EventCart from '../EventCart';
import { EventCartModal } from './EventCartModal';
import { CustomTemplateV2 } from '../CustomTemplateV2';
import { ModalConfirm } from './ModalConfirm';

const steps: TourSteps[] = [
  {
    title: 'Página do evento',
    description:
      'Crie uma página de vendas de forma gratuita para oferecer os ingressos do seu evento. Customize de acordo com sua identidade visual.',
    target: getTargetElement("div[data-node-key='event-page']")
  },
  {
    title: 'Página do evento',
    description:
      'Para uma página mais completa e profissional, selecione o modelo “Orbit Pages“. É grátis até 3 páginas criadas, aproveite!',
    target: getTargetElement('div#template-box-orbit'),
    placement: 'top',
    isLastStep: true
  }
];

export default function EventPage() {
  const { id: eventId } = useParams<PageParams>();
  const { data: eventPageData, loading, setEventData, setNewSaleUrl } = useEventDetail();
  const history = useHistory();
  const [cartModalOpen, setCartModalOpen] = useState(false);
  const showNew =
    isAfter(new Date(eventPageData?.event.created), new Date('2025-05-06 00:00:00')) &&
    eventPageData?.page_config?.templateVersion === 'v3';
  const { showTour, closeTour } = useTabTour('event-page');
  const isShown = useMemo(() => !loading && !!eventPageData, [loading, eventPageData]);

  const handleCustomTemplate = () => {
    history.push('event-page/template-basic');
  };

  const eventHasSalePage = eventId !== eventPageData?.advertisementLinks?.saleLink;
  const [showForm, setShowForm] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [isDeactivatingUrl, setIsDeactivatingUrl] = useState(false);

  const handlePageActive = (value: boolean) => {
    const saleUrl = eventPageData?.page_config?.sale_url ?? eventPageData?.advertisementLinks?.saleLink;

    if (!value && (saleUrl !== eventId || (saleUrl === eventId && eventPageData?.event?.marketplace_active))) {
      setOpenConfirm(true);
      return;
    }

    setShowForm(value);
  };

  const handleEventPageUrl = () => {
    const payload: Partial<IEventPost> = {
      ...eventPageData,
      sale_url: eventId,
      salespage_active: false,
      marketplace_active: false,
      page_config: {
        ...eventPageData.page_config,
        sale_url: eventId
      }
    };
    setIsDeactivatingUrl(true);
    eventService.edit(eventId, payload).subscribe({
      next: () => {
        setEventData(prevState => ({
          ...prevState,
          advertisementLinks: {
            ...prevState.advertisementLinks,
            saleLink: eventId
          },
          page_config: {
            ...prevState.page_config,
            sale_url: eventId
          },
          pageContent: {
            ...prevState.pageContent,
            sale_url: eventId
          },
          event: {
            ...prevState.event,
            sale_url: eventId,
            salespage_active: false,
            marketplace_active: false
          }
        }));
        setNewSaleUrl(eventId);
        notification.success({ message: 'Página de vendas desativada com sucesso.' });
        setShowForm(false);
        setOpenConfirm(false);
        setIsDeactivatingUrl(false);
      },
      error: () => {
        notification.error({ message: 'Houve um problema ao desativar a página. Tente novamente.' });
        setOpenConfirm(false);
        setIsDeactivatingUrl(false);
      }
    });
  };

  useEffect(() => {
    if (!eventPageData) return;

    if (eventPageData.event.salespage_active && showNew) {
      setShowForm(true);
      return;
    }

    setShowForm(false);
  }, [eventPageData, showNew]);

  return (
    <>
      <ReactPlaceholder customPlaceholder={<CustomLoader />} ready={!loading}>
        {isShown && (
          <>
            <TextAndSubText
              text='Página de vendas'
              subtext='Personalizar sua página de vendas reforça a identidade do seu evento e melhora a experiência dos
              participantes. Com a Blinket, você ativa ou desativa essa página quando quiser, de forma simples e
              prática.'
              level={5}
            />
            <br />
            {!showNew && (
              <>
                <div
                  id='template-box-orbit'
                  style={{ display: 'flex', alignItems: 'center', gap: '24px', marginBottom: '24px' }}
                >
                  <img src={TemplateOrbit} alt='Imagem do estilo de template Orbit' />
                  <a
                    href={eventPageData.partnerConfig.partnerEditUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    id='resume-live-goto-button'
                  >
                    Crie uma página de vendas focada em conversão em poucos minutos com o <b>Orbit Pages</b>
                  </a>
                </div>
                {!eventHasSalePage ? (
                  <span>
                    Ou crie uma página de vendas na Blinket <a onClick={handleCustomTemplate}>clicando aqui</a>
                  </span>
                ) : (
                  <span>
                    Você já tem uma página de vendas na Blinket{' '}
                    <a onClick={handleCustomTemplate}> Clique aqui para editar sua página</a>
                  </span>
                )}
              </>
            )}
            {showNew && (
              <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                <Switch value={showForm} onChange={handlePageActive} />
                <span>{showForm ? 'Página de vendas ativada' : 'Página de vendas desativada'}</span>
                <ModalConfirm
                  open={openConfirm}
                  onCancel={() => {
                    setOpenConfirm(false);
                    return;
                  }}
                  onConfirm={handleEventPageUrl}
                  loading={isDeactivatingUrl}
                />
              </div>
            )}
            {showForm && <CustomTemplateV2 eventId={eventId} eventPageData={eventPageData} />}
            <Tour steps={steps} open={showTour} onClose={closeTour} />
            <Divider />
            <TextAndSubText
              text='Já tem sua página de vendas? Integre nosso Widget!'
              subtext='Se você já possui sua própria página de vendas, facilite a experiência dos seus clientes integrando nosso Widget diretamente no seu site.'
              level={5}
            />
            <br />
            {!showNew && <EventCart />}
            {showNew && (
              <>
                <Button icon={<CodeOutlined />} onClick={() => setCartModalOpen(state => !state)} size='large'>
                  Abrir código
                </Button>
                <EventCartModal
                  eventId={eventId}
                  open={cartModalOpen}
                  onClose={() => setCartModalOpen(state => !state)}
                />
              </>
            )}
          </>
        )}
      </ReactPlaceholder>
    </>
  );
}
